import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

export interface ShiftStatusBreakdown {
    open: number;
    pending: number;
    approved: number;
    posted: number;
}

export interface SlotDetail {
    seqId: number;
    status: string;
    claimedByUserId: string | null;
    claimedByUserNm: string | null;
}

export interface ShiftDetail {
    requestId: number;
    requestType: string;
    requiredSkillCd: string;
    requiredSkillDesc: string;
    requiredTeamCd: string;
    startTime: string;
    endTime: string;
    postedByUserId: string;
    postedByUserNm: string;
    totalSlots: number;
    assignedUsers: string[];
    slotDetails: SlotDetail[];
}

export interface CalendarDay {
    date: string;
    dayOfWeek: string;
    totalShiftRequests: number;
    totalSlots: number;
    statusBreakdown: ShiftStatusBreakdown;
    shifts: ShiftDetail[];
}

export interface CalendarReportData {
    siteCd: string;
    startDate: string;
    endDate: string;
    totalDays: number;
    totalShifts: number;
    totalSlots: number;
    overallStatusBreakdown: ShiftStatusBreakdown;
    calendar: CalendarDay[];
}

interface CalendarReportState {
    data: CalendarReportData | null;
    loading: boolean;
    error: string | null;
    currentRequestId: string | null;
}

const initialState: CalendarReportState = {
    data: null,
    loading: false,
    error: null,
    currentRequestId: null,
};

/**
 * Fetch calendar report data
 * GET api/reports/calendar?siteCd=NY01&startDate=2026-05-01&endDate=2026-05-31
 */
export const fetchCalendarReport = createAsyncThunk(
    'calendarReport/fetchCalendarReport',
    async ({ siteCd, startDate, endDate }: { siteCd: string; startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(
                `${ENDPOINTS.GET_CALENDAR_REPORT}?siteCd=${siteCd}&startDate=${startDate}&endDate=${endDate}`
            );
            console.log('✅ Calendar Report response:', response);
            return response;
        } catch (error: any) {
            console.error('❌ Calendar Report API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch calendar report');
        }
    }
);

const calendarReportSlice = createSlice({
    name: 'calendarReport',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearCalendarReport: (state) => {
            state.data = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCalendarReport.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(fetchCalendarReport.fulfilled, (state, action) => {
                // Ignore stale responses from requests that have since been
                // superseded by a newer month/view navigation.
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchCalendarReport.rejected, (state, action) => {
                if (action.meta.requestId !== state.currentRequestId) return;
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError, clearCalendarReport } = calendarReportSlice.actions;
export default calendarReportSlice.reducer;